import { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://api.cloudnine.biz.id';

/**
 * Custom hook untuk mengambil dan mengelola data karakter dari API
 * 
 * @returns {Object} { characters, loading, error, refetch, addCharacter }
 */
export const useCharacters = () => {
    const [characters, setCharacters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Fetch semua karakter dari API
     */
    const fetchCharacters = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/characters.php`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setCharacters(result.data || []);
            } else {
                throw new Error(result.error || 'Failed to fetch characters');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching characters:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Tambah karakter baru via API
     * @param {Object} characterData - { name, race_id, title, tier, image }
     * @returns {Object|null} Karakter yang baru dibuat, atau null jika gagal
     */
    const addCharacter = async (characterData) => {
        try {
            const response = await fetch(`${API_BASE}/characters.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(characterData),
            });

            const result = await response.json();

            if (result.success) {
                // Refresh list setelah berhasil tambah
                await fetchCharacters();
                return result.data;
            } else {
                throw new Error(result.error || 'Failed to add character');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error adding character:', err);
            return null;
        }
    };

    /**
     * Update karakter via API
     * @param {Object} characterData - { id, name, race_id, title, tier, image }
     * @returns {Object|null} Karakter yang diupdate, atau null jika gagal
     */
    const updateCharacter = async (characterData) => {
        try {
            const response = await fetch(`${API_BASE}/characters.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(characterData),
            });

            const result = await response.json();

            if (result.success) {
                await fetchCharacters();
                return result.data;
            } else {
                throw new Error(result.error || 'Failed to update character');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error updating character:', err);
            return null;
        }
    };

    /**
     * Upload gambar karakter
     * @param {File} file - File gambar
     * @returns {string|null} URL gambar yang diupload, atau null jika gagal
     */
    const uploadImage = async (file) => {
        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await fetch(`${API_BASE}/upload.php`, {
                method: 'POST',
                body: formData,
            });

            // Handle non-JSON responses (e.g. PHP fatal errors)
            const text = await response.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch {
                console.error('Server response (not JSON):', text);
                throw new Error(`Server error (${response.status}): ${text.substring(0, 200)}`);
            }

            if (result.success) {
                return result.url;
            } else {
                throw new Error(result.error || 'Failed to upload image');
            }
        } catch (err) {
            console.error('Error uploading image:', err);
            throw err;
        }
    };

    // Fetch saat pertama kali mount
    useEffect(() => {
        fetchCharacters();
    }, [fetchCharacters]);

    return {
        characters,
        loading,
        error,
        refetch: fetchCharacters,
        addCharacter,
        updateCharacter,
        uploadImage,
    };
};
